import {
  EmrAddRounded,
  EmrHeart,
  SiCheck,
  SiEdit,
} from "@medstonetech/slate-icons";
import {
  Box,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Text,
  useDisclosure,
} from "@chakra-ui/react";

import { formMessages } from "messages";
import {
  ProgressFavoriteRequest,
  ProgressFavorite,
  useAddProgressFavorite,
  useProgressFavorites,
  useUpdateProgressFavorite,
  useDeleteProgressFavorite,
} from "modules/charts-shared/api";
import { ChangeEventHandler, useMemo, useState } from "react";

import { Button, DividerList, SearchBar, WarningDialog } from "shared";
import { debounce } from "throttle-debounce";
import * as Yup from "yup";
import { useToast, useYupValidationResolver } from "hooks";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { ChartRowElement } from "../chart";
import { extractApiErrorMessage } from "utils";
import { ProgressNoteType } from "enums";

const schema = Yup.object().shape({
  title: Yup.string().required(formMessages.required("Title")),
  progressNote: Yup.string().required(formMessages.required("Progress Note")),
});

type ProgressFavoritesModalProps = {
  isOpen: boolean;
  onClose(): void;
};

function ProgressFavoriteFormModal({
  isOpen,
  onClose,
  selected,
}: ProgressFavoritesModalProps & { selected?: ProgressFavorite }) {
  const toast = useToast();

  const deleteDisclosure = useDisclosure();

  const resolver = useYupValidationResolver(schema);
  const useFormContext = useForm<ProgressFavoriteRequest>({
    resolver,
    defaultValues: {
      title: selected?.title ?? "",
      progressNote: selected?.progressNote ?? "",
    },
  });

  const {
    handleSubmit,
    formState: { isValid },
  } = useFormContext;

  const { mutateAsync: addFavorite, isLoading } = useAddProgressFavorite();
  const { mutateAsync: updateFavorite, isLoading: isUpdateLoading } =
    useUpdateProgressFavorite(selected?.id ?? "");
  const { mutateAsync: deleteFavorite, isLoading: isDeleteLoading } =
    useDeleteProgressFavorite(selected?.id ?? "");

  const onSubmit: SubmitHandler<ProgressFavoriteRequest> = async (values) => {
    try {
      if (selected) {
        await updateFavorite(values);
        toast({ description: formMessages.updateSuccess("Favorite") });
      } else {
        await addFavorite(values);
        toast({ description: formMessages.createSuccess("Favorite") });
      }

      onClose();
    } catch (error) {
      toast({ description: extractApiErrorMessage(error) });
    }
  };

  const onDelete = async () => {
    try {
      await deleteFavorite(null);
      onClose();
    } catch (error) {
      toast({ description: extractApiErrorMessage(error) });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalContent background="gray.200" h="640px" w="540px">
        <FormProvider {...useFormContext}>
          <ModalHeader
            display="flex"
            flexDir="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Button variant="label" color="blue" onClick={onClose}>
              Cancel
            </Button>
            <Text fontSize="17px" fontWeight="600">
              My Progress Favorites
            </Text>
            <Button
              variant="label"
              color="blue"
              disabled={!isValid || isLoading || isUpdateLoading}
              onClick={handleSubmit(onSubmit)}
            >
              Save
            </Button>
          </ModalHeader>
          <ModalBody
            display="flex"
            flexDir="column"
            justifyContent="start"
            alignItems="start"
            gap={4}
          >
            <Box display="flex" flexDir="column" gap={2} w="100%">
              <Text color="gray.600" ml={4}>
                Title
              </Text>
              <ChartRowElement type="input" name="title" />
            </Box>
            <Box display="flex" flexDir="column" gap={2} w="100%">
              <Text color="gray.600" ml={4}>
                Progress Note
              </Text>
              <Box bgColor="white" borderRadius="10px">
                <ChartRowElement
                  type="text-area"
                  name="progressNote"
                  h="170px"
                  resize="none"
                  color="blue"
                />
              </Box>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="outlineSquared"
              borderRadius="25px"
              w="100%"
              sx={{
                color: "red",
                borderColor: "red",
              }}
              onClick={deleteDisclosure.onOpen}
              isLoading={isDeleteLoading}
              disabled={!selected}
            >
              Delete Favorite
            </Button>
            <WarningDialog
              isOpen={deleteDisclosure.isOpen}
              onCancel={deleteDisclosure.onClose}
              title="Warning!"
              mainText="Are you sure you want to Delete this Favorite? This action cannot be undone!"
              onClose={deleteDisclosure.onClose}
              onAction={onDelete}
              cancelLabel="Cancel"
              actionLabel="Delete"
              blockScrollOnMount={false}
              cancelButtonProps={{ color: "gray.700" }}
              actionButtonProps={{ color: "red" }}
            />
          </ModalFooter>
        </FormProvider>
      </ModalContent>
    </Modal>
  );
}

type ProgressFavoriteItemProps = {
  selected: boolean;
  favorite: ProgressFavorite;
  onSelectToEdit: () => void;
  onSelect: () => void;
};

function ProgressFavoriteItem({
  favorite,
  selected,
  onSelect,
  onSelectToEdit,
}: ProgressFavoriteItemProps) {
  return (
    <Box
      h="52px"
      w="100%"
      p={3}
      display="flex"
      justifyContent="space-between"
      alignItems="center"
    >
      <Box display="flex" alignItems="center" gap={3}>
        <IconButton
          aria-label="edit"
          icon={
            <Icon
              as={SiCheck}
              sx={{ color: selected ? "green" : "gray.450", fontSize: "24px" }}
            />
          }
          ml="6px"
          variant="icon"
          onClick={onSelect}
        />
        <Text fontWeight="600">{favorite.title}</Text>
      </Box>

      <IconButton
        aria-label="edit"
        icon={<Icon as={SiEdit} color="blue" sx={{ h: "20px", w: "20px" }} />}
        ml="6px"
        variant="icon"
        onClick={onSelectToEdit}
      />
    </Box>
  );
}

function ProgressFavoritesModal({
  isOpen,
  onClose,
  onAddNewNote,
}: ProgressFavoritesModalProps & ProgressFavoritesProps) {
  const { data, isLoading } = useProgressFavorites();

  const formDisclosure = useDisclosure();

  const [searchString, setSearchString] = useState<string | undefined>(
    undefined
  );
  const [selected, setSelected] = useState<ProgressFavorite | undefined>(
    undefined
  );
  const [selectedToEdit, setSelectedToEdit] = useState<
    ProgressFavorite | undefined
  >(undefined);

  const onSearch = useMemo(
    () =>
      debounce<ChangeEventHandler<HTMLInputElement>>(1000, (e) => {
        setSearchString(e.target.value || undefined);
      }),
    []
  );

  const favoritesFiltered =
    data?.data.filter((x) =>
      x.title
        .toLocaleLowerCase()
        .includes(searchString?.toLocaleLowerCase() ?? "")
    ) ?? [];

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalContent background="gray.200" h="640px" w="540px">
        <ModalHeader
          display="flex"
          flexDir="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Button variant="label" color="blue" onClick={onClose}>
            Close
          </Button>
          <Text fontSize="17px" fontWeight="600">
            My Progress Favorites
          </Text>
          <Button
            variant="label"
            color="blue"
            disabled={!selected}
            onClick={() => {
              onAddNewNote("note", selected?.progressNote ?? "");
              onClose();
            }}
            isLoading={isLoading}
          >
            Continue
          </Button>
        </ModalHeader>
        <ModalBody
          display="flex"
          flexDir="column"
          justifyContent="start"
          alignItems="start"
          gap={4}
        >
          <Button
            variant="label"
            color="blue"
            onClick={() => {
              setSelectedToEdit(undefined);
              formDisclosure.onOpen();
            }}
            gap={1}
          >
            <Icon as={EmrAddRounded} />
            Add New Favorite
          </Button>

          <SearchBar onChange={onSearch} placeholder="Search" />

          <Box borderRadius="10px" bgColor="white" w="100%">
            <DividerList dividerLeftMargin="48px">
              {favoritesFiltered.map((favorite) => (
                <ProgressFavoriteItem
                  key={favorite.id}
                  favorite={favorite}
                  selected={favorite.id === selected?.id}
                  onSelect={() =>
                    selected?.id === favorite.id
                      ? setSelected(undefined)
                      : setSelected(favorite)
                  }
                  onSelectToEdit={() => {
                    setSelectedToEdit(favorite);
                    formDisclosure.onOpen();
                  }}
                />
              ))}
            </DividerList>
          </Box>
        </ModalBody>
      </ModalContent>

      {formDisclosure.isOpen && (
        <ProgressFavoriteFormModal
          {...formDisclosure}
          selected={selectedToEdit}
        />
      )}
    </Modal>
  );
}

type ProgressFavoritesProps = {
  onAddNewNote: (type: ProgressNoteType, notes: string) => void;
  disabled?: boolean;
};

function ProgressFavorites(props: ProgressFavoritesProps) {
  const { disabled = false } = props;
  const modalDisclosure = useDisclosure();

  return (
    <Box>
      <Button
        variant="square"
        alignItems="center"
        disabled={disabled}
        onClick={() => modalDisclosure.onOpen()}
      >
        <Icon as={EmrHeart} fontSize="20px" mr="8px" />
        My Favorites
      </Button>
      {modalDisclosure.isOpen && (
        <ProgressFavoritesModal {...modalDisclosure} {...props} />
      )}
    </Box>
  );
}

export { ProgressFavorites };
